import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SypacButton, SypacText } from '@sypac/component-library-react';
import { OrderInterface } from '../../../../../../components/OrdersTable/interfaces/Order.interface';
import { OrdersService } from '../../../../../../services/orders.services';
import { OrderTarget } from '../../../../../../components/OrdersTable/interfaces/OrderStatus.interface';
import OrderDetailsStepper from '../../../../../../components/OrderDetailsStepper/OrderDetailsStepper';
import Emitter, { EventType } from '../../../../../../services/events';
import { T } from '@tolgee/react';
import PickUpDeliveryDetails from '../../AdditionalDetails/PickUp&DeliveryDetails';
import FulfilmentSchedule from '../../AdditionalDetails/FulfilmentSchedule';
import BaseOrderDetails from '../../../../../../components/BaseOrderDetails/BaseOrderDetails';
import LoadingSpinner from '../../../../../../assets/LoadingSpinner';
import { AdminOrderProps } from '../../../adminOrder.interface';
import OrderContractDetails from '../../../../../../components/OrderContractDetails/OrderContractDetails';

const AssignOrder: React.FC<AdminOrderProps> = (props) => {
	let { selectedOrder, setOrder, contract, hideViewContractButton } = props;
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [stepperMargin, setStepperMargin] = useState<number>(0);
	const [boxHeight, setBoxHeight] = useState<number>(0);
	const boxRef = useRef<HTMLDivElement>(null);

	const isSimpleOrder = useMemo(() => {
		return selectedOrder?.trucks?.length === 1;
	}, [selectedOrder?.trucks]);

	const deliveryIncluded = useMemo(() => {
		return selectedOrder?.deliveryIncluded;
	}, [selectedOrder?.deliveryIncluded]);

	const referenceFormatter = (id: string) => {
		return id.substring(0, 3) + ' ' + id.substring(3);
	};

	const handleClick = async () => {
		setIsLoading(true);
		try {
			await OrdersService.confirmOrder(selectedOrder?.id!, OrderTarget.admin);
			const { data } = await OrdersService.getOrders(
				{ ids: [selectedOrder?.id!] },
				'admin',
			);
			setOrder?.(data.items[0] as unknown as OrderInterface);
			Emitter.emit(EventType.ORDER_LIST_REFRESH, {
				id: selectedOrder?.id.toString(),
				recipientId: '',
				groupId: selectedOrder?.id.toString(),
				category: 'update_order',
			});
		} catch (e) {
		} finally {
			setIsLoading(false);
		}
	};

	const handleWidthChange = (value: number) => {
		setStepperMargin(value);
	};

	useEffect(() => {
		const element = boxRef.current;
		const resizeObserver = new ResizeObserver((entries) => {
			for (let entry of entries) {
				setBoxHeight(entry.contentRect.height);
			}
		});

		if (element) {
			resizeObserver.observe(element);
		}

		return () => {
			if (element) {
				resizeObserver.unobserve(element);
			}
		};
	}, []);

	const renderInitialSection = () => {
		return isSimpleOrder || !deliveryIncluded ? (
			<PickUpDeliveryDetails
				selectedOrder={selectedOrder}
				target={selectedOrder?.target}
				isPickUp={true}
				simpleOrder={isSimpleOrder}
			/>
		) : (
			<PickUpDeliveryDetails
				selectedOrder={selectedOrder}
				target={selectedOrder?.target}
				isPickUp={false}
				simpleOrder={isSimpleOrder}
			/>
		);
	};

	return (
		<div style={{ marginBottom: `${boxHeight}px` }}>
			<div style={{ marginBottom: `${stepperMargin}px` }}>
				<OrderDetailsStepper
					current={1}
					simpleOrder={isSimpleOrder}
					order={selectedOrder}
					onWidthChange={handleWidthChange}
				/>
			</div>

			<div className="px-10 pt-7 pb-3.5">
				{contract ? (
					<OrderContractDetails
						selectedOrder={selectedOrder}
						contract={contract}
						target="admin"
					/>
				) : (
					renderInitialSection()
				)}
			</div>

			{contract ? (
				<div className="px-10 py-3.5">{renderInitialSection()}</div>
			) : null}

			<div className="px-10 py-3.5">
				{isSimpleOrder || !deliveryIncluded ? (
					<PickUpDeliveryDetails
						selectedOrder={selectedOrder}
						target={selectedOrder?.target}
						isPickUp={false}
						simpleOrder={isSimpleOrder}
					/>
				) : (
					<PickUpDeliveryDetails
						selectedOrder={selectedOrder}
						target={selectedOrder?.target}
						isPickUp={true}
						simpleOrder={isSimpleOrder}
					/>
				)}
			</div>

			<div className="px-10 py-3.5">
				<BaseOrderDetails
					selectedOrder={selectedOrder}
					isOpen={true}
					simpleOrder={isSimpleOrder}
					deliveryIncluded={deliveryIncluded!}
					target={selectedOrder?.target!}
					contract={contract}
				/>
			</div>

			<div className="px-10 pb-7 pt-3.5">
				<FulfilmentSchedule
					selectedOrder={selectedOrder}
					step={1}
					deliveryIncluded={deliveryIncluded!}
					simpleOrder={isSimpleOrder}
				/>
			</div>

			{!hideViewContractButton && (
				<div
					className="fixed bottom-0 w-[702px] h-fit bg-alabaster border border-solid border-gray-40 border-l-0 border-r-0 border-b-0 rounded-2xl shadow-order-assign"
					ref={boxRef}
				>
					<div className="flex flex-col justify-center items-start p-8 gap-5">
						<SypacText variant="overline-regular-large">
							<p className="text-base leading-6 tracking-tighter text-gray-40">
								{selectedOrder?.deliveryIncluded ? (
									<T keyName="assignOrder.takeOnThisOrder">
										Take on this order.
									</T>
								) : (
									<div className="flex gap-4">
										<T keyName="assignOrder.orderTypeSelfService">
											Order type: Self-service
										</T>
										<div>·</div>
										{selectedOrder?.trucks?.length === 1 ? (
											<T keyName="assignOrder.singleOrder">Single order</T>
										) : (
											<T keyName="assignOrder.multipleOrder">Multiple orders</T>
										)}
									</div>
								)}
							</p>
						</SypacText>

						{selectedOrder?.deliveryIncluded ? (
							<SypacText variant="heading-5">
								<p className="tracking-tighter text-gray-90">
									<T keyName="assignOrder.thisOrderCurrentlyUnassigned">
										This order is currently unassigned
									</T>
								</p>
							</SypacText>
						) : null}

						{!selectedOrder?.deliveryIncluded &&
						selectedOrder?.trucks?.length === 1 ? (
							<SypacText variant="heading-5">
								<p className="tracking-tighter text-gray-90">
									<>
										<T keyName="assignOrder.referenceId">Reference ID:</T>{' '}
										{referenceFormatter(
											selectedOrder?.trucks! &&
												selectedOrder?.trucks[0].referenceId!,
										)}
									</>
								</p>
							</SypacText>
						) : null}

						<SypacText variant="overline-regular-large">
							<p className="text-base leading-6 tracking-tighter text-gray-40">
								{selectedOrder?.deliveryIncluded ? (
									<T keyName="assignOrder.acceptToManageAndNotify">
										Accept to manage it and notify matched carriers and sellers.
									</T>
								) : (
									<T keyName="assignOrder.assignThisOrderToProceed">
										Assign this order to proceed.
									</T>
								)}
							</p>
						</SypacText>

						<SypacButton variant="primary" size="large">
							<button
								className="w-fit transition"
								onClick={handleClick}
								disabled={isLoading}
							>
								<SypacText
									variant="body-normal-medium"
									className="flex items-center gap-4"
								>
									<p className="text-white mt-[3px]">
										{selectedOrder?.deliveryIncluded ? (
											<T keyName="assignOrder.acceptOrder">Accept order</T>
										) : (
											<T keyName="assignOrder.assignOrder">Assign order</T>
										)}
									</p>
									{isLoading ? <LoadingSpinner /> : null}
								</SypacText>
							</button>
						</SypacButton>
					</div>
				</div>
			)}
		</div>
	);
};

export default AssignOrder;
